/**
 * Invitations and activation (issue #78).
 *
 * The activation calls are unauthenticated by design: the person following the
 * link has no account yet, which is what the link is for. The tenant is
 * resolved server-side from the request's own origin, exactly as the public
 * apply form does, so there is no tenant to pass and no way to aim an
 * activation at a different school.
 */
import { api } from "@/lib/api/client";
import { commitOptions, retryOptions, type CommitReason } from "@/lib/api/commitSeams";

export type InvitationRole = "student" | "instructor" | "admin";

export type InvitationState =
  | "provisioning"
  | "failed"
  | "issued"
  | "claiming"
  | "accepted"
  | "revoked"
  | "superseded";

export type DeliveryState = "pending" | "sent" | "failed" | "not_configured" | "ambiguous";
export type CredentialSetupState =
  | "not_required"
  | "setup_email_required"
  | "setup_email_sent"
  | "complete"
  | "failed";

export interface InvitationProgress {
  workspace_account: { created: boolean; reused: boolean };
  lms_identity_bound: boolean;
  activation_token_created: boolean;
  email_accepted: boolean;
  email_failed: boolean;
  membership_created: boolean;
}

export interface InvitationSafeRetry {
  allowed: boolean;
  strategy: string;
}

export interface Invitation {
  id: string;
  version?: string;
  email: string;
  delivery_email: string;
  display_name: string | null;
  role: InvitationRole;
  student_id: string | null;
  applicant_id: string | null;
  /** The person a staff invitation is for (ADR 0034): redemption puts the login on them. */
  person_id?: string | null;
  state: InvitationState;
  /** Server-computed: only an issued, ready, delivered, unexpired link is usable. */
  usable?: boolean;
  provisioning_state: "legacy" | "pending" | "ready" | "failed";
  workspace_user_id: string | null;
  workspace_account_outcome: string | null;
  workspace_identity_mode?: "managed" | "observed_existing_admin";
  provisioned_user_id: string | null;
  super_admin_bootstrap: boolean;
  is_expired: boolean;
  expires_at: string;
  issued_at: string;
  issued_by: string | null;
  accepted_at: string | null;
  accepted_user_id: string | null;
  revoked_at: string | null;
  archived_at?: string | null;
  archived_by?: string | null;
  delivery_state: DeliveryState;
  delivery_attempts: number;
  last_delivery_at: string | null;
  last_delivery_error: string | null;
  /** The transport's own message id — what the office hands the provider when
   *  someone says the invitation never arrived. */
  last_delivery_ref: string | null;
  credential_setup_state: CredentialSetupState;
  credential_setup_failure_reason: string | null;
  progress?: InvitationProgress;
  safe_retry?: InvitationSafeRetry;
  provisioning_failure_reason?: string | null;
  student_name: string | null;
  student_sid: string | null;
  /** The named person a staff invitation is for, when it names one. */
  person_name?: string | null;
  managed_identity_id: string | null;
  managed_desired_state: "active" | "suspended" | null;
  managed_observed_state: "unknown" | "active" | "suspended" | "archived" | "missing" | null;
  managed_version: number | null;
  managed_incomplete: boolean | null;
  recovery_required: boolean;
}

export interface InvitationEvent {
  event: string;
  actor_user_id: string | null;
  occurred_at: string;
  outcome: string;
  detail: string | null;
}

export type InvitationRegisterView = "open" | "history";

export interface InvitationRegisterResponse {
  items: Invitation[];
  bootstrap_invitation: Invitation | null;
  view: InvitationRegisterView;
  page: number;
  page_size: number;
  matching: number;
  counts: { open: number; history: number; total: number };
  ranks?: import("./applicants").ApplicantRegister["ranks"];
}

export interface ManagedIdentityResolutionIssue {
  id: string;
  issue_kind: "provider_missing" | "provider_archived" | "ambiguous_binding";
  state: "open" | "resolved";
  detail: string;
  opened_at: string;
  invitation_version?: string | null;
  invitation_id: string | null;
  user_id: string | null;
  managed_email: string | null;
  desired_state: "active" | "suspended" | null;
  observed_state: "unknown" | "active" | "suspended" | "archived" | "missing" | null;
  recoverable: boolean;
  refusal_reason: string | null;
}

export interface IssuedInvitation {
  id: string;
  email: string;
  delivery_email: string;
  role: InvitationRole;
  state: string;
  delivery_state: DeliveryState;
  delivery_error: string | null;
  workspace_account: "created" | "reused";
  credential_setup_state: CredentialSetupState;
  progress: InvitationProgress;
  safe_retry: InvitationSafeRetry;
}

export interface IssueInvitationRequest {
  managed_email: string;
  delivery_email: string;
  given_name: string;
  family_name: string;
  display_name?: string | null;
  role: InvitationRole;
  student_id?: string | null;
  applicant_id?: string | null;
  /** An instructor or administrator invitation for a person who already has a record. */
  person_id?: string | null;
  confirm_admin_role?: boolean;
}

export interface ExistingWorkspaceAdminHandoff {
  id: string;
  source_invitation_id: string;
  entered: { name: string; managed_email: string };
  candidate: { name: string; managed_email: string };
  name_mismatch: boolean;
}

/**
 * The refusal contract for a managed invitation that stopped before delivery.
 * This is deliberately separate from `Invitation`: the response tells the
 * operator which effects happened during the failed attempt, while the list
 * keeps the durable invitation and audit history.
 */
export interface ManagedInvitationProvisioningFailure {
  code: "managed_invitation_provisioning_failed" | "existing_workspace_account_confirmation_required";
  message: string;
  invitation: { id: string; state: "failed"; usable: false };
  progress: InvitationProgress;
  safe_retry: InvitationSafeRetry;
  handoff?: ExistingWorkspaceAdminHandoff;
}

export function isManagedInvitationProvisioningFailure(
  detail: unknown,
): detail is ManagedInvitationProvisioningFailure {
  if (typeof detail !== "object" || detail === null) return false;
  const failure = detail as Partial<ManagedInvitationProvisioningFailure>;
  const progress = failure.progress;
  return (failure.code === "managed_invitation_provisioning_failed"
    || failure.code === "existing_workspace_account_confirmation_required")
    && typeof failure.message === "string"
    && typeof failure.invitation?.id === "string"
    && failure.invitation.state === "failed"
    && failure.invitation.usable === false
    && typeof progress === "object"
    && progress !== null
    && typeof progress.workspace_account?.created === "boolean"
    && typeof progress.workspace_account?.reused === "boolean"
    && typeof progress.lms_identity_bound === "boolean"
    && typeof progress.activation_token_created === "boolean"
    && typeof progress.email_accepted === "boolean"
    && typeof progress.email_failed === "boolean"
    && typeof progress.membership_created === "boolean"
    && typeof failure.safe_retry?.allowed === "boolean"
    && typeof failure.safe_retry.strategy === "string";
}

export function isExistingWorkspaceAdminHandoffFailure(
  detail: unknown,
): detail is ManagedInvitationProvisioningFailure & { handoff: ExistingWorkspaceAdminHandoff } {
  return isManagedInvitationProvisioningFailure(detail)
    && detail.code === "existing_workspace_account_confirmation_required"
    && typeof detail.handoff?.id === "string"
    && typeof detail.handoff.entered?.name === "string"
    && typeof detail.handoff.candidate?.name === "string"
    && typeof detail.handoff.candidate?.managed_email === "string"
    && typeof detail.handoff.name_mismatch === "boolean";
}

export interface ManagedIdentityRecoveryResult {
  invitation_id: string;
  managed_email: string;
  status: "complete" | "delivery_failed";
  delivery_state: DeliveryState;
  workspace_state: "active" | "suspended";
  detail: string;
}

export interface BootstrapSuperAdminRequest {
  delivery_email: string;
  given_name: string;
  family_name: string;
  display_name?: string | null;
}

export interface DesignatedSuperAdmin {
  invitation_id: string;
  user_id: string;
  is_super_admin: true;
}

/** The backend selects the host-bound tenant's fixed managed identity. */
export function bootstrapSuperAdmin(body: BootstrapSuperAdminRequest) {
  return api.post<IssuedInvitation>("/invitations/bootstrap/super-admin", body);
}

export function designateSuperAdmin(invitationId: string, expectedUserId: string) {
  return api.post<DesignatedSuperAdmin>(
    `/invitations/${invitationId}/designate-super-admin`,
    { expected_user_id: expectedUserId },
  );
}

/** Usable, or the reason it is not. Never both. */
export type InvitationPeek =
  | {
      usable: true;
      email: string;
      role: InvitationRole;
      tenant_name: string;
      redeem_with: "google" | "password";
    }
  | { usable: false; reason: string };

/**
 * A fresh setup email's outcome (W1): the invitation's own delivery record,
 * read after the send. Unknown is a send the server could not confirm; Check
 * reads the record again and never sends twice.
 */
export interface SetupEmailOutcome {
  state: "confirmed" | "refused" | "unknown";
  invitation_id: string;
  /** The invitation that now carries the link, once the send replaced it. */
  current_invitation_id: string | null;
  delivery_state: DeliveryState | null;
  reason: CommitReason | null;
}

export const invitationsApi = {
  list: () => api.get<Invitation[]>("/invitations"),

  register: (filters: {
    view: InvitationRegisterView;
    q?: string;
    page?: number;
    page_size?: number;
  }) => {
    const params = new URLSearchParams({
      view: filters.view,
      page: String(filters.page ?? 1),
      page_size: String(filters.page_size ?? 25),
    });
    if (filters.q?.trim()) params.set("q", filters.q.trim());
    return api.get<InvitationRegisterResponse>(`/invitations/register?${params.toString()}`);
  },

  registerDetail: (id: string) => api.get<Invitation>(`/invitations/register/${id}`),

  resolutionQueue: (issueId?: string) => api.get<ManagedIdentityResolutionIssue[]>(
    `/invitations/managed-identity-resolution-queue${issueId ? `?issue_id=${encodeURIComponent(issueId)}` : ""}`,
  ),

  issue: (body: IssueInvitationRequest) => api.post<IssuedInvitation>("/invitations", body),

  existingAdminHandoffs: () => api.get<ExistingWorkspaceAdminHandoff[]>(
    "/invitations/existing-workspace-handoffs",
  ),

  acceptExistingAdminHandoff: (id: string) => api.post<IssuedInvitation>(
    `/invitations/existing-workspace-handoffs/${id}/accept`,
    { confirm_existing_workspace_admin: true },
  ),

  rejectExistingAdminHandoff: (id: string) => api.post<{ id: string; state: "rejected" }>(
    `/invitations/existing-workspace-handoffs/${id}/reject`,
    { confirm_existing_workspace_admin: false },
  ),

  resend: (id: string, deliveryEmail?: string) => api.post<IssuedInvitation>(
    `/invitations/${id}/resend`,
    deliveryEmail ? { delivery_email: deliveryEmail } : {},
  ),

  /** One fresh setup email under a commit key: the delivery follows the recorded intent, once (W1). */
  sendSetupEmail: (id: string, key: string, version?: string, deliveryEmail?: string) =>
    api.post<SetupEmailOutcome>(`/invitations/${id}/resend`, deliveryEmail ? { delivery_email: deliveryEmail } : {}, version ? invitationOptions(key, version) : commitOptions(key)),

  issueCommit: (body: IssueInvitationRequest, key: string) => api.post<SingleInvitationResult>("/invitations", body, commitOptions(key)),
  bootstrapCommit: (body: BootstrapSuperAdminRequest, key: string) => api.post<SingleInvitationResult>("/invitations/bootstrap/super-admin", body, commitOptions(key)),
  resendBootstrapCommit: (id: string, version: string, key: string) => api.post<SingleInvitationResult>(`/invitations/${id}/resend-bootstrap`, {}, invitationOptions(key, version)),
  revokeCommit: (id: string, version: string, key: string) => api.post<SingleInvitationResult>(`/invitations/${id}/revoke`, {}, invitationOptions(key, version)),
  recoverCommit: (id: string, version: string, key: string) => api.post<SingleInvitationResult>(`/invitations/${id}/recover-managed-identity`, { confirm_recovery: true }, invitationOptions(key, version)),

  /**
   * Fresh setup emails for a selection (R6, CASE-029): one key for the whole
   * send; a retry carries the same key and only the invitations that failed.
   */
  sendSetupEmails: (ids: readonly string[], key: string, retry = false) =>
    api.post<unknown>("/invitations/resend", { invitation_ids: ids }, retry ? retryOptions(key) : commitOptions(key)),

  // Bootstrap identities are existing Workspace Super Admins. This path only
  // replaces their IMAM link after read-only exact-ID verification and never
  // invokes the ordinary managed-identity lifecycle recovery.
  resendBootstrap: (id: string) => api.post<IssuedInvitation>(
    `/invitations/${id}/resend-bootstrap`,
    {},
  ),

  verifyManagedIdentity: (id: string) => api.post<{
    observed_state: "active" | "suspended" | "archived" | "missing";
    recovery_required: boolean;
  }>(`/invitations/${id}/verify-managed-identity`, {}),

  recoverManagedIdentity: (id: string) => api.post<ManagedIdentityRecoveryResult>(
    `/invitations/${id}/recover-managed-identity`,
    { confirm_recovery: true },
  ),

  revoke: (id: string) => api.post<{ id: string; state: string }>(`/invitations/${id}/revoke`, {}),

  archiveHistory: (id: string) => api.post<{ invitation_id: string; archived_count: number }>(
    `/invitations/${id}/archive-history`,
    {},
  ),

  events: (id: string) => api.get<InvitationEvent[]>(`/invitations/${id}/events`),

  /** Read-only despite being a POST: it does NOT claim the invitation, so
   *  opening or refreshing the activation page never burns the window the form
   *  needs. POST because a token in a query string is written to access logs,
   *  kept in browser history, and forwarded in `Referer`. */
  peek: (token: string) => api.post<InvitationPeek>("/public/invitation", { token }),

  activate: (body: { token: string; password: string; full_name?: string | null }) =>
    api.post<{ activated: boolean; email: string; role: InvitationRole }>(
      "/public/activate",
      body,
    ),

  redeem: (token: string) =>
    api.post<{ activated: true; user_id: string }>("/public/invitation/redeem", { token }),
};

/** What an admin needs to see about a link that is no longer usable. Computed
 *  here rather than stored, so an invitation never reads as expired while it is
 *  still inside its own validity window. */
export function hasActiveFailedProvisioning(
  invitation: Pick<Invitation, "state" | "provisioning_state">,
): boolean {
  return invitation.provisioning_state === "failed"
    && (invitation.state === "provisioning" || invitation.state === "failed");
}

export function invitationStatusLabel(invitation: Invitation): string {
  if (invitation.recovery_required) return "Recovery required";
  if (hasActiveFailedProvisioning(invitation)) return "Provisioning failed";
  if (invitation.state === "accepted") return "Activated";
  if (invitation.delivery_state === "ambiguous") return "Delivery outcome unknown";
  if (invitation.credential_setup_state === "failed") return "Setup email failed";
  if (invitation.state === "revoked") return "Revoked";
  if (invitation.state === "superseded") return "Replaced";
  if (invitation.is_expired) return "Expired";
  if (invitation.credential_setup_state === "setup_email_required") return "Setup email required";
  if (invitation.credential_setup_state === "setup_email_sent") return "Recipient setup pending";
  if (invitation.credential_setup_state === "complete") return "Activation complete";
  if (invitation.state === "claiming") return "Activating";
  if (invitation.state === "provisioning") return "Provisioning";
  return "Waiting";
}


export interface SingleInvitationResult {
  state: "confirmed" | "unknown" | "refused";
  invitation_id: string | null;
  current_invitation_id?: string | null;
  delivery_state: DeliveryState | null;
  reason: CommitReason | null;
}
function invitationOptions(key: string, version: string) {
  return commitOptions(key, { "If-Match": version });
}
